import { bool, func, node, number, oneOf, string } from "prop-types";
import React, { useState } from "react";

import styled from "styled-components";

import { AnimatePresence, motion } from "framer-motion";

import { useAnimationStyles } from "./stateHooks";

const Padding = styled("div")`
  padding: ${({ $gap }) => $gap / 4}px 0px;
`;

const StaggerMotion = ({
  children,
  delayIn,
  delayOut,
  setHasEntered,
  direction,
  variant,
  snapHeight,
}) => {
  const animationStyles = useAnimationStyles({
    direction,
    delayIn,
    delayOut,
    variant,
    snapHeight,
  });
  return (
    <motion.div
      {...animationStyles}
      onAnimationComplete={() => setHasEntered(true)}
    >
      {children}
    </motion.div>
  );
};

StaggerMotion.propTypes = {
  children: node,
  delayIn: number,
  delayOut: number,
  setHasEntered: func.isRequired,
  direction: oneOf(["top", "bottom"]),
  variant: string,
  snapHeight: bool,
};

/**
 *
 * @param {object} param0
 * @param {number} param0.interval - the space between the entrance of each child
 * @param {number} param0.delay - how long to wait before the first child enters
 * @param {boolean} param0.show - should the children be showing?
 * @param {boolean} param0.initOnly - only animates on initial render
 * @param {boolean} param0.noInit - never animates on initial render
 * @param {number} param0.gap - For smoother animations, use this instead of an actual flex gap
 * @param {'scale'|'fade'} param0.variant - access different animation effect.
 * @param {boolean} param0.snapHeight - turns off height animation. Used for when container is already animating the height.
 * @returns
 */
const FnrStaggeredEntry = ({
  interval = 0.05,
  delay = 0,
  show,
  muted,
  initOnly,
  noInit,
  gap,
  children,
  direction,
  variant = "scale",
  snapHeight,
}) => {
  const [hasEntered, setHasEntered] = useState();
  if (!children) return null;
  if (muted) return children;
  if (hasEntered && initOnly) return children;

  return (
    <AnimatePresence initial={!noInit}>
      {show
        ? React.Children.map(children, (child, idx) => {
            const isChild = Boolean(child);
            const delayIn = interval * idx + delay;
            const delayOut =
              interval * Math.abs(idx - (children.length - 1)) + delay;
            return (
              <StaggerMotion
                key={child?.key || idx}
                delayIn={delayIn}
                delayOut={delayOut}
                setHasEntered={setHasEntered}
                direction={direction}
                variant={variant}
                snapHeight={snapHeight}
              >
                {gap > 0 && idx !== 0 && isChild && <Padding $gap={gap} />}
                {child}
                {gap > 0 && idx !== children.length - 1 && isChild && (
                  <Padding $gap={gap} />
                )}
              </StaggerMotion>
            );
          })
        : null}
      ;
    </AnimatePresence>
  );
};

FnrStaggeredEntry.propTypes = {
  children: node,
  delay: number,
  interval: number,
  show: bool,
  muted: bool,
  initOnly: bool,
  noInit: bool,
  gap: number,
  snapHeight: bool,
  direction: oneOf(["top", "bottom"]),
  variant: oneOf(["scale", "fade"]),
};

export default FnrStaggeredEntry;
